/**
 * Display labels for the enum values the backend emits (tech names + facial
 * areas). The backend ships enums, not copy; the frontend renders the words.
 * The maps are `satisfies Partial<Record<...>>` so a key that isn't a real
 * TechName/Area fails to compile (they are keyed straight off the payload).
 * P1 inlines an `en` map; a full i18n namespace is a later polish step.
 */
import type { Area, TechName } from '@see-stella/types'

const METRIC_LABELS = {
  hydration: 'Hydration',
  texture_roughness: 'Texture',
  redness: 'Redness',
  surface_sebum: 'Oiliness',
  telangiectasia: 'Visible vessels',
  breakouts: 'Breakouts',
  pores: 'Pores',
  enlarged_pores: 'Enlarged pores',
  pie: 'Post-inflammatory redness',
  pigmentation: 'Pigmentation',
  pigmentation_composite: 'Pigmentation',
  sun_spots: 'Sun spots',
  melasma: 'Melasma',
  freckles: 'Freckles',
  lines: 'Lines',
  fine_lines: 'Fine lines',
  deep_lines: 'Deep lines',
  nasolabial_wrinkles: 'Nasolabial lines',
  marionette_lines: 'Marionette lines',
  sagging: 'Sagging',
  jowls: 'Jowls',
  age: 'Skin age',
  dark_circles: 'Dark circles',
  puffiness: 'Puffiness',
  eye_bags: 'Eye bags',
  lacrimal_grooves: 'Tear troughs',
  eyes_age: 'Eye-area age',
  skin_type: 'Skin type',
  ita: 'Skin tone',
  skintone: 'Skin tone',
  sensitivity_composite: 'Sensitivity',
  inflammation: 'Inflammation',
  irritation: 'Irritation',
  pimples: 'Pimples',
  uniformness: 'Evenness',
  translucency: 'Translucency',
  moles: 'Moles',
} satisfies Partial<Record<TechName, string>>

const AREA_LABELS = {
  face: 'Whole face',
  nose: 'Nose',
  forehead: 'Forehead',
  left_eye: 'Eye area (L)',
  right_eye: 'Eye area (R)',
  left_cheek: 'Cheek (L)',
  right_cheek: 'Cheek (R)',
  perioral: 'Mouth area',
  upper_lip: 'Upper lip',
  chin: 'Chin',
  jawline: 'Jawline',
  t_zone: 'T-zone',
  u_zone: 'U-zone',
} satisfies Partial<Record<Area, string>>

const METRIC_MAP = new Map<string, string>(Object.entries(METRIC_LABELS))
const AREA_MAP = new Map<string, string>(Object.entries(AREA_LABELS))

/** Prettify an unknown snake_case enum as a last resort. */
function prettify(value: string): string {
  const s = value.replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export function labelForMetric(techName: string): string {
  return METRIC_MAP.get(techName) ?? prettify(techName)
}

export function labelForArea(area: string): string {
  return AREA_MAP.get(area) ?? prettify(area)
}

// Human-facing labels for routine slots (the backend ships the enum).
const SLOT_LABELS: Record<string, string> = {
  makeup_remover: 'Makeup remover',
  cleanser: 'Cleanser',
  exfoliant: 'Exfoliant',
  toner: 'Toner',
  serum: 'Serum',
  spot_treatment: 'Spot treatment',
  face_mask: 'Face mask',
  eye_treatment: 'Eye treatment',
  lip_treatment: 'Lip treatment',
  moisturizer: 'Moisturizer',
  sunscreen: 'Sunscreen',
  aftersun: 'Aftersun',
  neck_treatment: 'Neck treatment',
}

export function labelForSlot(slot: string | null | undefined): string {
  if (!slot) return ''
  return SLOT_LABELS[slot] ?? prettify(slot)
}
